"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const OffboardingContext = createContext({
  offboardings: [],
});

export const OffboardingProvider = ({ children }) => {
  const [offboardings, setOffboardings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ==========================
  // Fetch Offboardings
  // ==========================
  const fetchOffboardings = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/offboarding`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch offboarding records");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setOffboardings(data.data || []);
      } else {
        setError(data.message || "Failed to fetch offboarding records");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch offboarding records");
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // Get Single Offboarding
  // ==========================
  const getOffboarding = async (id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/offboarding/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to fetch offboarding record",
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to fetch offboarding record",
      };
    }
  };

  // ==========================
  // Create Offboarding
  // payload: { user_id, resignation_date, last_working_day, reason, status, notes }
  // ==========================
  const createOffboarding = async (payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/offboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to create offboarding request",
        };
      }

      if (data.status === 201 || data.status === 200) {
        await fetchOffboardings();

        toast.success(data.message || "Offboarding request submitted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create offboarding request",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to create offboarding request",
      };
    }
  };

  // ==========================
  // Update Offboarding
  // ==========================
  const updateOffboarding = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/offboarding/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to update offboarding record",
        };
      }

      if (data.status === 200) {
        await fetchOffboardings();

        toast.success(data.message || "Offboarding record updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update offboarding record",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update offboarding record",
      };
    }
  };

  // ==========================
  // Delete Offboarding
  // ==========================
  const deleteOffboarding = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/offboarding/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to delete offboarding record",
        };
      }

      if (data.status === 200) {
        await fetchOffboardings();

        toast.success(data.message || "Offboarding record deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete offboarding record",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to delete offboarding record",
      };
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchOffboardings();
    }
  }, []);

  return (
    <OffboardingContext.Provider
      value={{
        offboardings,
        loading,
        error,
        fetchOffboardings,
        getOffboarding,
        createOffboarding,
        updateOffboarding,
        deleteOffboarding,
        setOffboardings,
      }}
    >
      {children}
    </OffboardingContext.Provider>
  );
};

export const useOffboardings = () => useContext(OffboardingContext);